import { useEffect, useRef, useState, useCallback } from 'react';
import { Phase } from './useBreathingRhythm';

export const useBreathingSound = (phase: Phase, duration: number, isActive: boolean) => {
    const [isMuted, setIsMuted] = useState(false);
    const audioContextRef = useRef<AudioContext | null>(null);
    const gainNodeRef = useRef<GainNode | null>(null);
    const filterNodeRef = useRef<BiquadFilterNode | null>(null);
    const audioInitializedRef = useRef(false);

    // Initialize Audio Context
    const initAudio = useCallback(() => {
        if (!audioContextRef.current) {
            const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
            audioContextRef.current = new AudioContextClass();
        }

        if (!audioInitializedRef.current && audioContextRef.current) {
            const ctx = audioContextRef.current;

            // 1. Create a Noise Source (Pink Noise is better for breath than Brown)
            // We'll generate a 4 second buffer slightly to avoid evident looping
            const bufferSize = ctx.sampleRate * 4;
            const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
            const data = buffer.getChannelData(0);

            // Pink Noise Generation (Paul Kellet's refined method)
            let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
            for (let i = 0; i < bufferSize; i++) {
                const white = Math.random() * 2 - 1;
                b0 = 0.99886 * b0 + white * 0.0555179;
                b1 = 0.99332 * b1 + white * 0.0750759;
                b2 = 0.96900 * b2 + white * 0.1538520;
                b3 = 0.86650 * b3 + white * 0.3104856;
                b4 = 0.55000 * b4 + white * 0.5329522;
                b5 = -0.7616 * b5 - white * 0.0168980;
                data[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
                data[i] *= 0.11; // Structure gain
                b6 = white * 0.115926;
            }

            const noiseSource = ctx.createBufferSource();
            noiseSource.buffer = buffer;
            noiseSource.loop = true;

            // 2. High Pass Filter (Remove muddy lows, breath is airy)
            const highPass = ctx.createBiquadFilter();
            highPass.type = 'highpass';
            highPass.frequency.value = 100;

            // 3. Dynamic Filter (Bandpass/Lowpass) to simulate mouth shape
            // This is the one we will modulate during inhale/exhale
            filterNodeRef.current = ctx.createBiquadFilter();
            filterNodeRef.current.type = 'lowpass';
            filterNodeRef.current.Q.value = 0.6; // Gentle resonance
            filterNodeRef.current.frequency.value = 800; // Starting midpoint

            // 4. Master Gain (Volume Envelope)
            gainNodeRef.current = ctx.createGain();
            gainNodeRef.current.gain.value = 0;


            // Connect Graph: Source -> HP -> DynamicFilter -> Gain -> Destination
            noiseSource.connect(highPass);
            highPass.connect(filterNodeRef.current);
            filterNodeRef.current.connect(gainNodeRef.current);
            gainNodeRef.current.connect(ctx.destination);

            noiseSource.start();
            audioInitializedRef.current = true;
        }

        if (audioContextRef.current?.state === 'suspended') {
            audioContextRef.current.resume();
        }
    }, []);

    // Managing the sound loop (handled by init mostly, just state check here)
    // Managing the sound loop (handled by init mostly, just state check here)
    useEffect(() => {
        if (isActive && !isMuted) {
            initAudio();
        }

        // Cleanup on unmount or when stopped
        return () => {
            if (audioContextRef.current) {
                // Close the context to stop all processing and release hardware
                // We don't just suspend, we close because the hook lifecycle is ending
                audioContextRef.current.close().then(() => {
                    audioContextRef.current = null;
                    audioInitializedRef.current = false;
                });
            }
        };
    }, [isActive, isMuted, initAudio]);

    // Modulating Sound based on Phase
    useEffect(() => {
        if (!gainNodeRef.current || !filterNodeRef.current || !audioContextRef.current || isMuted) {
            if (gainNodeRef.current && audioContextRef.current) {
                gainNodeRef.current.gain.cancelScheduledValues(audioContextRef.current.currentTime);
                gainNodeRef.current.gain.setTargetAtTime(0, audioContextRef.current.currentTime, 0.1);
            }
            return;
        }

        const now = audioContextRef.current.currentTime;
        const gain = gainNodeRef.current.gain;
        const filter = filterNodeRef.current;

        // Cancel any scheduled changes
        gain.cancelScheduledValues(now);
        filter.frequency.cancelScheduledValues(now);

        // Human Breath Characteristics:
        // Inhale: Higher airflow, slightly sharper (higher cutoff), ramping up intensity.
        // Exhale: Warmer (lower cutoff), longer decay, relaxing.

        const maxVolume = 0.8;

        if (phase === 'inhale') {
            // Inhale: Rise volume, open filter
            // Start from silence
            gain.setTargetAtTime(maxVolume, now, duration / 3); // Reach volume faster

            // Filter modulation: Open up (simulating filling lungs/throat constriction noise)
            filter.frequency.setValueAtTime(400, now);
            filter.frequency.linearRampToValueAtTime(1200, now + duration);

        } else if (phase === 'exhale') {
            // Exhale: Start volume high, fade out slowly
            gain.setValueAtTime(maxVolume, now);
            gain.linearRampToValueAtTime(0, now + duration);

            // Filter modulation: Close down (relaxing)
            filter.frequency.setValueAtTime(1000, now);
            filter.frequency.linearRampToValueAtTime(300, now + duration);

        } else if (phase === 'hold' || phase === 'holdAfter') {
            gain.setTargetAtTime(0, now, 0.2); // Fast fade to silence
        } else {
            gain.setTargetAtTime(0, now, 0.5);
        }

    }, [phase, duration, isMuted]);

    const toggleMute = () => {
        if (audioContextRef.current?.state === 'suspended') {
            audioContextRef.current.resume();
        }
        setIsMuted(prev => !prev);
    };

    return { isMuted, toggleMute };
};
